import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useProductOffering } from 'features/product/components';
import { Reporter } from 'features/dataCapture';
import decodeHtml from 'utilities/decodeHtml';
import Currency from 'components/Currency';
import { utagLinkPDP } from 'features/tealium';
import { setGenericEvent } from 'features/tealium/ga4/custom';

import isMappedTo from '../utility/isMappedTo';
import getRelationshipItemDetailClass from '../utility/getRelationshipItemDetailClass';
import { relationshipItemDetailProp } from '../types';
import getFormattedItemLabel from '../utility/getFormattedItemLabel';
import { getPrimaryCategoryFromUtag } from '../utility';

import './RelationshipItemDetail.scss';

const RelationshipItemDetail = ({
	activeShortSku,
	label,
	itemLabel,
	shortSku,
	relationshipItemDetail: {
		displayPrice, imageUrl, productName, productUrl, heroImageUrl, skuInputType
	},
	handleMouseEnter,
	handleMouseLeave,
	interfaceType,
	position
}) => {

	const dataCaptureReporter = window.lp.dataCapture.reporter;

	const productOffering = useProductOffering((currentRef) => {

		if (
			!dataCaptureReporter.isSkuViewRecorded(currentRef)
			&& shortSku.toUpperCase() !== activeShortSku.toUpperCase()
		) {

			const data = Reporter.getSkuViewData(currentRef);
			dataCaptureReporter.track(data);

		}

	});

	const relationshipItemDetailClass = getRelationshipItemDetailClass(label, interfaceType);
	const showThumbnail = isMappedTo.displayOptions.showThumbnail(label);
	const showLabel = isMappedTo.displayOptions.showLabel(label);
	const showPrice = isMappedTo.displayOptions.showPrice(label);
	const displayArea = isMappedTo.displayOptions.displayArea.which(label);

	const isActive = activeShortSku === shortSku.toUpperCase();
	const activeClassName = 'RelationshipItemDetail--active';

	const formattedItemLabel = useMemo(() => getFormattedItemLabel(itemLabel, label), [ label ]);

	const handleGAClick = () => {

		utagLinkPDP()({
			eventAction: `Click-${label}-Variation`,
			eventLabel: formattedItemLabel
		});

		setGenericEvent({
			event_name: 'pdp_variation_click',
			pdp_item_id: activeShortSku,
			pdp_prime_cat: getPrimaryCategoryFromUtag(),
			item_position_number: position,
			pdp_var_value: `${label}:${formattedItemLabel}`
		});

	};

	return (
		<div
			className={classNames(relationshipItemDetailClass, { [activeClassName]: isActive })}
			ref={productOffering.setRefs}
			data-sku={shortSku.toUpperCase()}
			data-sku-input-type={skuInputType}
		>
			<a
				className="RelationshipItemDetail__imageWrapper"
				href={productUrl}
				onClick={handleGAClick}
				onMouseEnter={
					typeof handleMouseEnter === 'function'
						? () => handleMouseEnter(heroImageUrl, productName, formattedItemLabel)
						: undefined
				}
				onMouseLeave={typeof handleMouseLeave === 'function' ? () => handleMouseLeave() : undefined}
			>
				{showThumbnail && <img height={96} width={96} src={imageUrl} alt={decodeHtml(productName)} />}
				{showLabel && displayArea === 'insideButton' && (
					<div
						className={classNames(
							'RelationshipItemDetail__itemLabel RelationshipItemDetail__itemLabel--inside',
							!showPrice ? 'RelationshipItemDetail__itemLabel--labelOnlyInside' : ''
						)}
					>
						{formattedItemLabel}
					</div>
				)}
				{showPrice && displayArea === 'insideButton' && (
					<div className="RelationshipItemDetail__itemPrice RelationshipItemDetail__itemPrice--inside">
						<Currency price={displayPrice} superscriptCents />
					</div>
				)}
			</a>
			{showLabel && displayArea === 'outsideButton' && (
				<div className="RelationshipItemDetail__itemLabel">
					{formattedItemLabel}
				</div>
			)}
			{showPrice && displayArea === 'outsideButton' && (
				<div className="RelationshipItemDetail__itemPrice">
					<Currency price={displayPrice} superscriptCents />
				</div>
			)}
		</div>
	);

};
RelationshipItemDetail.propTypes = {
	activeShortSku: PropTypes.string,
	label: PropTypes.string,
	itemLabel: PropTypes.string,
	shortSku: PropTypes.string,
	relationshipItemDetail: relationshipItemDetailProp,
	handleMouseEnter: PropTypes.func,
	handleMouseLeave: PropTypes.func,
	interfaceType: PropTypes.number,
	position: PropTypes.number
};
export default RelationshipItemDetail;
